import { useGetTeam } from "@/hooks/queryHooks/teamHooks";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import type { SubmitterTemplate, UserWRole } from "@repo/types";
import { ComboInput } from "@repo/ui/components/Base/ComboInput";
import { useMemo } from "react";

export interface DocumentTemplateSubmittersPanelProps {
  tenantId: string;
  teamId: string;
  submitters: SubmitterTemplate[];
  disabled?: boolean;
  onEmailChange: (uuid: string, email: string) => void;
  onEmailBlur: () => void | Promise<void>;
}

export const DocumentTemplateSubmittersPanel = ({
  tenantId,
  teamId,
  submitters,
  disabled,
  onEmailChange,
  onEmailBlur,
}: DocumentTemplateSubmittersPanelProps) => {
  const { data: teamData } = useGetTeam(tenantId, teamId);
  const { data: userData } = useUserList(tenantId);

  const userMap = useMemo(
    () =>
      userData?.reduce(
        (obj, user) => ((obj[user.id] = user), obj),
        Object.create(null) as Record<string, UserWRole>
      ),
    [userData]
  );

  const options = useMemo(
    () =>
      teamData?.teamMembers
        .map((member) => {
          const user = userMap?.[member.userId];
          if (!user?.email) return null;

          return {
            value: user.email,
            label: `${user.firstName} ${user.lastName} (${user.email})`,
          };
        })
        .filter(
          (option): option is { value: string; label: string } => !!option
        ) ?? [],
    [teamData?.teamMembers, userMap]
  );

  return (
    <div className="w-80 shrink-0 rounded-md border p-4 overflow-y-auto max-h-[70vh]">
      <p className="font-semibold">Submitters</p>
      <div className="mt-4 flex flex-col gap-4">
        {submitters.length ? (
          submitters.map((submitter) => (
            <div key={submitter.uuid} className="space-y-2">
              <p className="text-sm font-medium">{submitter.name}</p>
              <ComboInput
                id={`submitter-email-${submitter.uuid}`}
                autoComplete={"email-" + submitter.uuid}
                disabled={disabled}
                value={submitter.email ?? ""}
                placeholder="Email"
                options={options}
                onChange={(evt) => {
                  onEmailChange(submitter.uuid, evt.target.value);
                }}
                onBlur={onEmailBlur}
              />
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">
            Submitters will appear here after the DocuSeal form loads.
          </p>
        )}
      </div>
    </div>
  );
};
